import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { IIdentityTenant } from '../../../../data/types';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ErrorText } from '../../../shared/form/ErrorMessage';

export interface ITenantSelectionProps {
  tenants: IIdentityTenant[];
  selectedTenant: string | null;
  onSelectTenant: (orgId: string | null) => void;
  error?: string;
}

export const TenantSelection = React.memo(
  (props: ITenantSelectionProps): React.ReactElement => {
    const handleTenantClick = React.useCallback(
      (orgId: string): void => {
        if (props.selectedTenant === orgId) {
          props.onSelectTenant(null);
        } else {
          props.onSelectTenant(orgId);
        }
      },
      [props.selectedTenant, props.onSelectTenant]
    );

    const errorId = 'tenant-error';

    return (
      <React.Fragment>
        <h1 id='tenant-heading' style={{ margin: '.5rem 1rem' }}>
          Multiple Organizations Found
        </h1>
        <p
          id='tenant-description'
          style={{ margin: '0 1rem', color: '#888', fontSize: '0.9rem' }}
        >
          Select the organization you want to sign in to:
        </p>
        <InputContainer
          role='form'
          aria-labelledby='tenant-heading'
          aria-describedby='tenant-description'
        >
          <ControlSpacer style={{ gap: '10px' }} role='radiogroup'>
            {props.tenants.map(
              (tenant: IIdentityTenant, index: number): React.ReactElement => {
                const isSelected = props.selectedTenant === tenant.org_id;
                const displayName = tenant.name || tenant.org_id;

                return (
                  <TenantCard
                    key={tenant.org_id}
                    selected={isSelected}
                    onClick={(): void => handleTenantClick(tenant.org_id)}
                    animationDelay={index}
                    role='radio'
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent): void => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTenantClick(tenant.org_id);
                      }
                    }}
                  >
                    <TenantName>{displayName}</TenantName>
                    <TenantSubtitle>{tenant.app_config.name}</TenantSubtitle>
                  </TenantCard>
                );
              }
            )}
          </ControlSpacer>
          {props.error && (
            <ErrorText id={errorId} role='alert' aria-live='polite'>
              {props.error}
            </ErrorText>
          )}
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<ITenantSelectionProps>,
    nextProps: Readonly<ITenantSelectionProps>
  ): boolean => {
    return (
      prevProps.tenants === nextProps.tenants &&
      prevProps.selectedTenant === nextProps.selectedTenant &&
      prevProps.error === nextProps.error &&
      prevProps.onSelectTenant === nextProps.onSelectTenant
    );
  }
);

TenantSelection.displayName = 'TenantSelection';

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  padding-top: 10px;
  width: 100%;
  height: 100%;
`;

const TenantCard = styled.div<{ selected: boolean; animationDelay: number }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 2px solid ${(props): string => (props.selected ? '#0066cc' : '#ddd')};
  border-radius: 8px;
  background-color: ${(props): string => (props.selected ? '#f0f7ff' : '#fff')};
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeSlideIn} 400ms ease-out;
  animation-delay: ${(props): string => `${props.animationDelay * 80}ms`};
  animation-fill-mode: both;

  &:hover {
    border-color: #0066cc;
    background-color: #f0f7ff;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 102, 204, 0.1);
  }

  &:focus {
    outline: 2px solid #0066cc;
    outline-offset: 2px;
  }
`;

const TenantName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const TenantSubtitle = styled.div`
  font-size: 0.85rem;
  color: #666;
`;
